<script setup lang="ts">
import { computed, provide, reactive, useId } from "vue";
import { ErrorText } from "../../ErrorText";
import { HelperText } from "../../HelperText";

interface RadioGroupPropsBase {
  /** Disables the radio group and prevents user interaction. */
  disabled?: boolean;
  /** Displays an optional helper text below the radio group. */
  helperText?: string;
  modelValue?: string;
  name?: string;
  orientation?: "horizontal" | "vertical";
}

interface RadioGroupPropsError extends RadioGroupPropsBase {
  error: boolean;
  errorText?: string;
}

interface RadioGroupPropsValid extends RadioGroupPropsBase {
  error?: boolean;
  errorText?: never;
}

export type RadioGroupProps = RadioGroupPropsError | RadioGroupPropsValid;

const modelValue = defineModel<string>("modelValue");
const {
  disabled = false,
  error = false,
  errorText,
  helperText,
  name,
  orientation = "vertical",
} = defineProps<RadioGroupProps>();

// Create a 'name' if none is given
const computedName = computed(() => name || `radio-group-${useId()}`);

// To get the two-way binding working, it is necessary to create a reactive object. This is cause
// our binding will get lost while using provide.
const radioGroupContext = reactive({
  disabled,
  error,
  modelValue,
  name: computedName,
});

provide("radioGroupContext", radioGroupContext);
</script>

<template>
  <div class="clr-radio-group flex flex-col flex-wrap gap-y-1">
    <div
      class="flex flex-wrap gap-x-5 gap-y-2"
      :class="{ 'flex-col': orientation === 'vertical' }"
      role="radiogroup"
    >
      <slot />
    </div>
    <HelperText v-if="helperText">
      {{ helperText }}
    </HelperText>
    <ErrorText v-if="!disabled && error && errorText?.trim()">
      {{ errorText }}
    </ErrorText>
  </div>
</template>
